import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { RecordRTCPromisesHandler } from 'recordrtc';
import { RecordingService } from '../services/RecordingService';
import { useNavigate } from 'react-router-dom';

interface RecordingState {
  isRecording: boolean;
  isProcessing: boolean;
  isPreparing: boolean;
  showCountdown: boolean;
  mode: 'audio' | 'video' | null;
  stream: MediaStream | null;
  recorder: RecordRTCPromisesHandler | null;
  recordingTime: number;
  recordingUrl: string | null;
  recordingId: string | null;
  vocalsOnlyUrl: string | null;
  localBlobUrl: string | null;
  error: string | null;
  success: string | null;
  syncTimestamp: number | null;
}

interface RecordingContextType {
  recording: RecordingState;
  startRecording: (mode: 'audio' | 'video', videoStream?: MediaStream, backingTrackUrl?: string, wavesurfer?: WaveSurfer) => Promise<void>;
  stopRecording: (songId: string, onCameraStop?: () => void, wavesurfer?: WaveSurfer, tournamentId?: string | null, tournamentRound?: number | null) => Promise<void>;
  saveRecording: (songId: string, tournamentId?: string | null, tournamentRound?: number | null) => Promise<{ recordingId: string; recordingUrl: string; vocalsOnlyUrl: string | null }>;
  setProcessing: (processing: boolean) => void;
  deleteRecording: () => Promise<void>;
  clearError: () => void;
  clearSuccess: () => void;
  onCountdownComplete: () => void;
}

const RecordingContext = createContext<RecordingContextType | undefined>(undefined);

export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (!context) {
    throw new Error('useRecording must be used within a RecordingProvider');
  }
  return context;
};

export const RecordingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const recordingService = new RecordingService();
  const [recording, setRecording] = useState<RecordingState>({
    isRecording: false,
    isProcessing: false,
    isPreparing: false,
    showCountdown: false,
    mode: null,
    stream: null,
    recorder: null,
    recordingTime: 0,
    recordingUrl: null,
    recordingId: null,
    vocalsOnlyUrl: null,
    localBlobUrl: null,
    error: null,
    success: null,
    syncTimestamp: null
  });
  const backingTrackRef = useRef<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const preparationRecorderRef = useRef<RecordRTCPromisesHandler | null>(null);
  const preparationStreamRef = useRef<MediaStream | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const modeRef = useRef<'audio' | 'video' | null>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (recording.isRecording) {
      interval = setInterval(() => {
        setRecording(prev => ({
          ...prev,
          recordingTime: prev.recordingTime + 1
        }));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [recording.isRecording]);

  const isRecordingRef = useRef(false);
  const recorderRef = useRef<RecordRTCPromisesHandler | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    isRecordingRef.current = recording.isRecording;
    recorderRef.current = recording.recorder;
    streamRef.current = recording.stream;
  }, [recording.isRecording, recording.recorder, recording.stream]);

  useEffect(() => {
    const handleDeviceChange = async () => {
      if (!isRecordingRef.current) return;

      try {
        if (recorderRef.current) {
          await recorderRef.current.stopRecording();
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        if (wavesurferRef.current) {
          wavesurferRef.current.pause();
        }
      } catch (_) {}

      setRecording(prev => ({
        ...prev,
        isRecording: false,
        isProcessing: false,
        isPreparing: false,
        showCountdown: false,
        mode: null,
        stream: null,
        recorder: null,
        recordingTime: 0,
        recordingUrl: null,
        recordingId: null,
        vocalsOnlyUrl: null,
        localBlobUrl: null,
        error: 'Recording stopped: an audio device was connected or disconnected. Please try again.',
        syncTimestamp: null
      }));

      backingTrackRef.current = null;
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (recording.isRecording && recording.recorder) {
        recording.recorder.stopRecording();
      }
      if (recording.stream) {
        recording.stream.getTracks().forEach(track => track.stop());
      }
      if (preparationStreamRef.current) {
        preparationStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const prepareRecording = async (mode: 'audio' | 'video', videoStream?: MediaStream, backingTrackUrl?: string) => {
    try {

      const constraints = {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        },
        video: false
      };

      let stream: MediaStream;

      if (mode === 'video' && videoStream) {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });
        stream = new MediaStream([...videoStream.getTracks(), ...audioStream.getTracks()]);
        videoStreamRef.current = videoStream;
      } else {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      preparationStreamRef.current = stream;

      if (backingTrackUrl) {
        backingTrackRef.current = backingTrackUrl;
        if (wavesurferRef.current) {
          wavesurferRef.current.seekTo(0);
        }
      }

      setRecording(prev => ({
        ...prev,
        showCountdown: true
      }));

    } catch (error) {
      console.error('Error during preparation:', error);

      if (preparationStreamRef.current) {
        preparationStreamRef.current.getTracks().forEach(track => track.stop());
        preparationStreamRef.current = null;
      }

      setRecording(prev => ({
        ...prev,
        isPreparing: false,
        showCountdown: false,
        error: 'Failed to access microphone or camera. Please check your device permissions and try again.'
      }));

      throw error;
    }
  };

  const countdownCompletedRef = useRef(false);

  const onCountdownComplete = useCallback(async () => {
    if (countdownCompletedRef.current) return;
    countdownCompletedRef.current = true;

    try {

      setRecording(prev => ({
        ...prev,
        showCountdown: false
      }));

      const mode = modeRef.current;
      const stream = preparationStreamRef.current;

      if (!mode || !stream) {
        throw new Error('Recording mode or stream not available');
      }

      const recorder = new RecordRTCPromisesHandler(stream, {
        type: mode === 'audio' ? 'audio' : 'video',
        mimeType: mode === 'audio' ? 'audio/webm;codecs=opus' : 'video/webm;codecs=vp8,opus',
        disableLogs: true,
        numberOfAudioChannels: 2,
        desiredSampRate: 44100,
        audioBitsPerSecond: 128000,
        ...(mode === 'video' && {
          frameInterval: 1,
          quality: 'high'
        })
      });

      const syncTimestamp = Date.now();

      await recorder.startRecording();

      if (wavesurferRef.current) {
        wavesurferRef.current.seekTo(0);
        wavesurferRef.current.play().catch(() => {
          // Ignore AbortError — thrown when play() is interrupted by a concurrent pause()
        });
      }

      setRecording(prev => ({
        ...prev,
        isRecording: true,
        isPreparing: false,
        mode,
        stream,
        recorder,
        recordingTime: 0,
        syncTimestamp
      }));

      preparationStreamRef.current = null;

    } catch (error) {
      console.error('Error starting synchronized recording:', error);
      setRecording(prev => ({
        ...prev,
        isPreparing: false,
        showCountdown: false,
        error: 'Failed to start recording. Please try again.'
      }));
    }
  }, []);

  /**
   * Start recording
   *
   * @param mode - 'audio' or 'video' recording mode
   * @param videoStream - Optional video stream for video recording
   * @param backingTrackUrl - The record track URL (vocals-only backing track from audio_url)
   * @param wavesurfer - WaveSurfer instance for audio synchronization
   */
  const startRecording = async (mode: 'audio' | 'video', videoStream?: MediaStream, backingTrackUrl?: string, wavesurfer?: WaveSurfer) => {
    try {

      countdownCompletedRef.current = false;

      if (wavesurfer) {
        wavesurferRef.current = wavesurfer;
      }

      modeRef.current = mode;

      setRecording(prev => ({
        ...prev,
        isPreparing: true
      }));

      await prepareRecording(mode, videoStream, backingTrackUrl);

    } catch (error) {
      console.error('Error starting recording:', error);
      setRecording(prev => ({
        ...prev,
        isPreparing: false,
        error: 'Failed to start recording. Please check your device permissions.'
      }));
    }
  };

  // Store context for saveRecording (called later when user submits)
  const pendingSaveRef = useRef<{
    songId: string;
    tournamentId?: string | null;
    tournamentRound?: number | null;
  } | null>(null);

  const stopRecording = async (songId: string, onCameraStop?: () => void, wavesurfer?: WaveSurfer, tournamentId?: string | null, tournamentRound?: number | null) => {
    if (!recording.recorder || !recording.stream || !songId || recording.isProcessing) {
      return;
    }

    // Store params for later save
    pendingSaveRef.current = { songId, tournamentId, tournamentRound };

    try {
      recording.stream.getTracks().forEach(track => {
        track.stop();
      });

      if (recording.mode === 'video') {
        document.querySelectorAll('video').forEach(video => {
          if (video.srcObject instanceof MediaStream) {
            video.srcObject.getTracks().forEach(track => {
              track.stop();
            });
            video.srcObject = null;
          }
        });
        if (onCameraStop) {
          onCameraStop();
        }
      }

      if (wavesurfer) wavesurfer.pause();

      // Get the blob immediately so we can show playback right away
      await recording.recorder.stopRecording();
      const blob = await recording.recorder.getBlob();

      if (blob.size === 0) {
        throw new Error('No audio was recorded. Please check your microphone and try again.');
      }

      const localUrl = URL.createObjectURL(blob);

      // Show the review screen immediately with local blob -- no upload yet
      setRecording(prev => ({
        ...prev,
        isRecording: false,
        isProcessing: false,
        localBlobUrl: localUrl
      }));

    } catch (error) {
      console.error('Error stopping recording:', error);
      setRecording(prev => ({
        ...prev,
        isRecording: false,
        isProcessing: false,
        isPreparing: false,
        showCountdown: false,
        mode: null,
        stream: null,
        recorder: null,
        recordingTime: 0,
        recordingUrl: null,
        recordingId: null,
        vocalsOnlyUrl: null,
        localBlobUrl: null,
        error: error instanceof Error ? error.message : 'Failed to save recording',
        syncTimestamp: null
      }));
    }
  };

  // Upload the recording to storage + database (called when user clicks Submit)
  const saveRecording = async (songId: string, tournamentId?: string | null, tournamentRound?: number | null): Promise<{ recordingId: string; recordingUrl: string; vocalsOnlyUrl: string | null }> => {
    if (!recording.recorder || !recording.localBlobUrl) {
      throw new Error('No recording to save');
    }

    setRecording(prev => ({ ...prev, isProcessing: true }));

    try {
      console.log('[SCORING] Calling stopAndSaveRecording...');
      const blob = await recording.recorder.getBlob();
      const { url: recordingUrl, id: recordingId, vocalsOnlyUrl } = await recordingService.stopAndSaveRecording(
        blob,
        songId,
        recording.mode!,
        recording.recordingTime,
        backingTrackRef.current || null,
        tournamentId,
        tournamentRound
      );
      console.log('[SCORING] stopAndSaveRecording returned:', { recordingId, recordingUrl: recordingUrl?.substring(0, 80) });

      setRecording(prev => ({
        ...prev,
        isProcessing: false,
        recordingUrl,
        recordingId,
        vocalsOnlyUrl,
        success: 'Recording saved successfully!'
      }));

      backingTrackRef.current = null;
      pendingSaveRef.current = null;

      return { recordingId, recordingUrl, vocalsOnlyUrl };
} catch (error) {
      console.error('Error saving recording:', error);
      setRecording(prev => ({
        ...prev,
        isProcessing: false,
        error: error instanceof Error ? error.message : 'Failed to save recording'
      }));
      throw error;
    }
  };

  const setProcessing = (processing: boolean) => {
    setRecording(prev => ({
      ...prev,
      isProcessing: processing
    }));
  };

  const deleteRecording = async () => {
    if (recording.recordingId) {
      try {
        await recordingService.deleteRecording(recording.recordingId);
      } catch (err) {
        console.error('Error deleting recording:', err);
      }
    }

    if (recording.localBlobUrl) {
      URL.revokeObjectURL(recording.localBlobUrl);
    }

    setRecording({
      isRecording: false,
      isProcessing: false,
      isPreparing: false,
      showCountdown: false,
      mode: null,
      stream: null,
      recorder: null,
      recordingTime: 0,
      recordingUrl: null,
      recordingId: null,
      vocalsOnlyUrl: null,
      localBlobUrl: null,
      error: null,
      success: null,
      syncTimestamp: null
    });
  };

  const clearError = () => {
    setRecording(prev => ({ ...prev, error: null }));
  };

  const clearSuccess = () => {
    setRecording(prev => ({ ...prev, success: null }));
  };

  return (
    <RecordingContext.Provider value={{
      recording,
      startRecording,
      stopRecording,
      saveRecording,
      setProcessing,
      deleteRecording,
      clearError,
      clearSuccess,
      onCountdownComplete
    }}>
      {children}
    </RecordingContext.Provider>
  );
};
